import Signal from '@getgo/signal';
import { consumeDataObject, consumeUnstructuredArgs, flattenDataProperty, } from './helpers/log_formatting_helpers';
function processLogCall(logger, level, args) {
    var _a;
    let msg;
    const root = logger.root;
    root.signals.onRawLog.dispatch({
        level: level,
        arguments: args,
        module: logger,
    });
    // Grab data object if present
    let data = consumeDataObject(args);
    // Consider first arg as message if string
    if (args.length > 0 && typeof args[0] === 'string') {
        msg = args.shift();
        // Grab data object left after message string
        data = consumeDataObject(args);
    }
    // No structured data, let's build it from the args if any
    // and attempt to log them as cleanly as possible
    if (!data && args.length > 0) {
        data = consumeUnstructuredArgs(args);
    }
    // Let's build our log entry. The order in which properties are added is kept in server logs
    // args should be an empty array by now, re-add structured data for local targets
    const logEntry = {
        // First put logging context in the log entry. args don't need those
        seqNum: root.seqNum++,
        level,
        timestamp: new Date().toJSON(),
    };
    // Then add module info if any
    // args get context data after moduleName and msg
    // log entry gets context data before them
    const moduleName = logger.fullNodeName;
    // Put moduleName and msg in the args
    if (moduleName)
        args.push(moduleName);
    if (msg !== undefined)
        args.push(msg);
    // Add context data to args and log entry
    const moduleContext = logger.getFullNodeContext(false);
    Object.keys(moduleContext).forEach((prop) => {
        logEntry[prop] = moduleContext[prop];
        args.push(prop + '=', moduleContext[prop]);
    });
    // Put moduleName and msg in the log entry
    if (moduleName)
        logEntry.module = moduleName;
    if (msg !== undefined)
        logEntry.msg = msg;
    // Add the rendered config to the log entry
    const config = (_a = logger.getConfig) === null || _a === void 0 ? void 0 : _a.call(logger);
    if (config)
        logEntry.config = config;
    // Finally, make sure we have a user data object to work with then flatten it
    data = data || {};
    Object.keys(data).forEach(function forEachDataProp(prop) {
        flattenDataProperty(logEntry, prop, data[prop]);
        args.push(prop + '=', data[prop]);
    });
    // Errors in targets "should" be handled by the targets themselves
    root.signals.onLineLog.dispatch({
        level: level,
        arguments: args,
    });
    root.signals.onFormattedLog.dispatch({
        // Object for future extensibility
        entry: logEntry,
    });
}
function tryLog(logger, level, args) {
    let fullNodeName;
    try {
        processLogCall(logger, level, args);
    }
    catch (error) {
        try {
            try {
                fullNodeName = logger.fullNodeName;
            }
            catch (ignoreError) {
                // Ignore a broken fullNodeName getter
            }
            logger.root.signals.onError.dispatch({
                source: 'message',
                type: 'error',
                detail: {
                    error,
                    module: fullNodeName,
                },
            });
        }
        catch (nestedError) {
            setTimeout(function rethrowNestedError() {
                throw nestedError;
            }, 0);
        }
    }
}
function createLogFunction(level) {
    return function log() {
        const argslen = arguments.length;
        const args = new Array(argslen);
        for (let i = 0; i < argslen; ++i) {
            // eslint-disable-next-line prefer-rest-params
            args[i] = arguments[i];
        }
        tryLog(this, level, args);
    };
}
// eslint-disable-next-line @typescript-eslint/no-empty-function
function Logger() { }
Logger.prototype.log = createLogFunction('log');
Logger.prototype.warn = createLogFunction('warn');
Logger.prototype.info = createLogFunction('info');
Logger.prototype.error = createLogFunction('error');
Logger.prototype.debug = createLogFunction('debug');
Logger.prototype.flush = function flush() {
    const root = this.root;
    if (root && root !== this)
        root.flush();
};
function getLoggerMethodOverrides() {
    return {
        setContext: function setContext(prop, value) {
            setContext._super.call(this, prop, value, flattenDataProperty);
        },
    };
}
// Logger Root
function LoggerRoot() {
    this.seqNum = 1;
    this.signals = Object.assign(this.signals || {}, {
        onRawLog: new Signal(),
        onLineLog: new Signal(),
        onFormattedLog: new Signal(),
        onFlush: new Signal(),
        onError: new Signal(),
    });
}
LoggerRoot.prototype = Object.create(Logger.prototype, {
    constructor: {
        value: LoggerRoot,
    },
});
LoggerRoot.prototype.flush = function flush() {
    this.signals.onFlush.dispatch();
};
export default {
    pluginName: 'logger',
    dependencies: ['nodeName', 'context'],
    nodeMethods: Logger.prototype,
    getNodeMethodOverrides: getLoggerMethodOverrides,
    rootInit: LoggerRoot,
    rootMethods: LoggerRoot.prototype,
};
//# sourceMappingURL=logger.js.map